import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import { resolveMedia, useSettings } from '../../context/SettingsContext';
import { DEFAULT_LOGO } from '../../config/brand';

function Icon({ name, className = 'h-5 w-5' }) {
  const paths = {
    shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/>,
    truck: <><path d="M1 4h14v13H1zM15 9h4l4 4v4h-8z"/><circle cx="6" cy="19" r="2"/><circle cx="18" cy="19" r="2"/></>,
    return: <><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="1"/><path d="m3 7 9 6 9-6"/></>,
    phone: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 2 .7 2.9a2 2 0 0 1-.4 2.1L8.1 10a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.9.6 2.9.7a2 2 0 0 1 1.6 1.9Z"/>,
    location: <><path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/></>,
    instagram: <><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r=".7" fill="currentColor" stroke="none"/></>,
    facebook: <path d="M14 8h4V3h-4c-4 0-6 2-6 6v3H5v5h3v5h5v-5h4l1-5h-5V9c0-1 .3-1 1-1Z"/>,
    arrow: <path d="M5 12h14m-5-5 5 5-5 5"/>,
  };
  return <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

const shopLinks = [
  ['Sandalwood idols', '/shop?category=car-dashboard-idols'],
  ['Japa malas', '/shop?category=malas'],
  ['Bracelets', '/shop?category=bracelets'],
  ['Incense', '/shop?category=agarbatti'],
  ['Keychains', '/shop?category=keychains'],
  ['Gift bundles', '/shop?category=combos'],
];

const supportLinks = [
  ['My orders', '/orders'],
  ['Authenticity', '/authenticity'],
  ['Care guide', '/care-guide'],
  ['Returns and refunds', '/returns-and-refunds'],
  ['Contact support', '/contact'],
];

export default function Footer() {
  const { settings } = useSettings();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const contact = settings?.contact || {};
  const siteName = settings?.siteName || 'Chandana Aura';
  const tagline = settings?.tagline || 'The Fragrance of Tradition';
  const logo = settings?.logoDark || settings?.logo || DEFAULT_LOGO;
  const freeShipping = settings?.shippingSummary?.freeShippingAboveAmount;

  useEffect(() => {
    api.get('/settings/public-payment').then(({ data }) => {
      const enabled = [];
      if (data.razorpay?.isEnabled) enabled.push('Razorpay');
      if (data.stripe?.isEnabled) enabled.push('Stripe');
      if (data.paypal?.isEnabled) enabled.push('PayPal');
      if (data.upi?.isEnabled) enabled.push('UPI');
      if (data.bankTransfer?.isEnabled) enabled.push('Bank transfer');
      if (data.cod?.isEnabled) enabled.push('Cash on delivery');
      setPaymentMethods(enabled);
    }).catch(() => setPaymentMethods([]));
  }, []);

  return (
    <footer className="border-t border-[#32322F] bg-[#191918] text-[#F2F0EC]">
      <div className="border-b border-[#343431] bg-[#20201E]">
        <div className="mx-auto grid max-w-7xl grid-cols-3 px-4 sm:px-6">
          {[
            ['shield', 'Authenticity assured', 'Certificate included'],
            ['truck', freeShipping ? `Free above ₹${freeShipping}` : 'Pan-India delivery', 'Secure dispatch'],
            ['return', 'Seven-day returns', 'Simple support'],
          ].map(([icon, title, detail], index) => <div key={title} className={`flex min-h-[76px] items-center justify-center gap-2 border-r border-[#343431] px-2 text-center last:border-r-0 sm:gap-3 sm:text-left ${index === 1 ? 'bg-[#252522]' : ''}`}><Icon name={icon} className="hidden h-5 w-5 shrink-0 text-[#C5A057] sm:block"/><div><p className="text-[9px] font-bold text-[#F2F0EC] sm:text-[11px]">{title}</p><p className="mt-0.5 text-[8px] text-[#A9A49D] sm:text-[9px]">{detail}</p></div></div>)}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-12">
        <div className="grid gap-9 md:grid-cols-12 md:gap-7">
          <section className="md:col-span-5 lg:col-span-4">
            <Link to="/" className="inline-flex flex-col items-start" aria-label={`${siteName} home`}>
              <span className="rounded-md bg-[#F7F3EC] px-3 py-2"><img src={resolveMedia(logo)} alt={siteName} className="h-12 w-auto max-w-[220px] object-contain"/></span>
              <span className="mt-2 block text-[9px] text-[#A9A49D]">{tagline}</span>
            </Link>
            <p className="mt-5 max-w-sm text-[11px] leading-6 text-[#B9B4AD]">Authentic Mysuru sandalwood objects, thoughtfully sourced and finished by skilled craftspeople for ritual, everyday use, and gifting.</p>
            <Link to="/authenticity" className="mt-5 inline-flex items-center gap-2 border-b border-[#8E7139] pb-1 text-[10px] font-bold uppercase text-[#D2B16D]">Read our authenticity promise <Icon name="arrow" className="h-3.5 w-3.5"/></Link>
            {(contact.instagram || contact.facebook) && <div className="mt-6 flex gap-2">{contact.instagram && <a href={contact.instagram} target="_blank" rel="noreferrer" className="grid h-9 w-9 place-items-center rounded-full border border-[#464641] text-[#C8C3BC] hover:border-[#C5A057] hover:text-[#C5A057]" title="Instagram" aria-label="Instagram"><Icon name="instagram" className="h-4 w-4"/></a>}{contact.facebook && <a href={contact.facebook} target="_blank" rel="noreferrer" className="grid h-9 w-9 place-items-center rounded-full border border-[#464641] text-[#C8C3BC] hover:border-[#C5A057] hover:text-[#C5A057]" title="Facebook" aria-label="Facebook"><Icon name="facebook" className="h-4 w-4"/></a>}</div>}
          </section>

          <nav className="grid grid-cols-2 gap-7 md:col-span-4" aria-label="Footer navigation">
            <div><h2 className="text-[10px] font-bold uppercase text-[#D2B16D]">Shop</h2><ul className="mt-4 space-y-2.5">{shopLinks.map(([label, to]) => <li key={to}><Link to={to} className="text-[11px] text-[#B9B4AD] hover:text-white">{label}</Link></li>)}</ul></div>
            <div><h2 className="text-[10px] font-bold uppercase text-[#D2B16D]">Help</h2><ul className="mt-4 space-y-2.5">{supportLinks.map(([label, to]) => <li key={to}><Link to={to} className="text-[11px] text-[#B9B4AD] hover:text-white">{label}</Link></li>)}</ul></div>
          </nav>

          <section className="border-t border-[#343431] pt-7 md:col-span-3 md:border-l md:border-t-0 md:pl-7 md:pt-0 lg:col-span-4 lg:pl-10">
            <h2 className="text-[10px] font-bold uppercase text-[#D2B16D]">Contact</h2>
            <div className="mt-4 space-y-3 text-[11px] text-[#B9B4AD]">
              <a href={`mailto:${contact.email || 'support@chandanaaura.com'}`} className="flex items-start gap-3 hover:text-white"><Icon name="mail" className="mt-0.5 h-4 w-4 shrink-0 text-[#C5A057]"/><span className="break-all">{contact.email || 'support@chandanaaura.com'}</span></a>
              <a href={`tel:${contact.phone || '+919876543210'}`} className="flex items-start gap-3 hover:text-white"><Icon name="phone" className="mt-0.5 h-4 w-4 shrink-0 text-[#C5A057]"/><span>{contact.phone || '+91 98765 43210'}</span></a>
              {contact.address && <p className="flex items-start gap-3"><Icon name="location" className="mt-0.5 h-4 w-4 shrink-0 text-[#C5A057]"/><span className="leading-5">{contact.address}</span></p>}
            </div>
            <div className="mt-7"><p className="text-[9px] font-bold uppercase text-[#8F8A83]">Available payment methods</p><div className="mt-3 flex flex-wrap gap-x-3 gap-y-2">{paymentMethods.length > 0 ? paymentMethods.map((method) => <span key={method} className="border-l-2 border-[#647A68] pl-2 text-[9px] font-semibold text-[#D1CDC7]">{method}</span>) : <span className="border-l-2 border-[#647A68] pl-2 text-[9px] font-semibold text-[#D1CDC7]">Secure checkout</span>}</div></div>
          </section>
        </div>
      </div>

      <div className="border-t border-[#343431] bg-[#151514]">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-5 text-[9px] text-[#8F8A83] sm:px-6 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} {siteName}. All rights reserved.</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2"><Link to="/privacy-policy" className="hover:text-white">Privacy</Link><Link to="/terms-and-conditions" className="hover:text-white">Terms</Link><Link to="/returns-and-refunds" className="hover:text-white">Returns</Link><Link to="/contact" className="hover:text-white">Support</Link></div>
        </div>
      </div>
    </footer>
  );
}
